/**
 * Tag Registry — single source of truth for all annotation domains, sub-skills, and tools.
 *
 * Every tag in the system comes from here. No hardcoded domain lists elsewhere.
 * Tags are stored in `individual.tags text[]` and `job.tags text[]` in the database.
 *
 * Categories:
 *   - Domain: top-level annotation expertise areas
 *   - [DomainName]: sub-skills within a domain
 *   - Tool: annotation platforms and tools
 *   - Language: language/locale expertise
 */

export const TAG_REGISTRY = [
  // ─── Core Annotation Domains ───
  { id: 'Audio', color: '#5DCAA5', category: 'Domain' },
  { id: 'Video', color: '#85B7EB', category: 'Domain' },
  { id: 'Visual', color: '#AFA9EC', category: 'Domain' },
  { id: 'Linguistics', color: '#FAC775', category: 'Domain' },
  { id: 'Music', color: '#ED93B1', category: 'Domain' },
  { id: 'Computer Use', color: '#85B7EB', category: 'Domain' },
  { id: 'Data Science', color: '#F0997B', category: 'Domain' },
  { id: 'AI Evaluation', color: '#95C4B5', category: 'Domain' },
  { id: 'Hardware Annotations', color: '#E8A87C', category: 'Domain' },
  { id: 'Medical', color: '#ED93B1', category: 'Domain' },
  { id: 'Legal', color: '#AFA9EC', category: 'Domain' },
  { id: 'Financial', color: '#FAC775', category: 'Domain' },

  // ─── Audio Sub-skills ───
  { id: 'Foley', color: '#5DCAA5', category: 'Audio' },
  { id: 'Speech Recognition', color: '#5DCAA5', category: 'Audio' },
  { id: 'Music Production', color: '#5DCAA5', category: 'Audio' },
  { id: 'Sound Design', color: '#5DCAA5', category: 'Audio' },
  { id: 'Audio QA', color: '#5DCAA5', category: 'Audio' },
  { id: 'Speaker Identification', color: '#5DCAA5', category: 'Audio' },
  { id: 'Transcription', color: '#5DCAA5', category: 'Audio' },

  // ─── Video Sub-skills ───
  { id: 'VFX', color: '#85B7EB', category: 'Video' },
  { id: 'Motion Capture', color: '#85B7EB', category: 'Video' },
  { id: 'Video Editing', color: '#85B7EB', category: 'Video' },
  { id: 'Temporal Annotation', color: '#85B7EB', category: 'Video' },
  { id: 'Action Recognition', color: '#85B7EB', category: 'Video' },
  { id: 'Scene Segmentation', color: '#85B7EB', category: 'Video' },

  // ─── Visual Sub-skills ───
  { id: 'Image Classification', color: '#AFA9EC', category: 'Visual' },
  { id: 'Object Detection', color: '#AFA9EC', category: 'Visual' },
  { id: 'Semantic Segmentation', color: '#AFA9EC', category: 'Visual' },
  { id: 'Instance Segmentation', color: '#AFA9EC', category: 'Visual' },
  { id: 'Bounding Box', color: '#AFA9EC', category: 'Visual' },
  { id: 'OCR', color: '#AFA9EC', category: 'Visual' },
  { id: 'Keypoint Detection', color: '#AFA9EC', category: 'Visual' },

  // ─── Linguistics Sub-skills ───
  { id: 'Translation', color: '#FAC775', category: 'Linguistics' },
  { id: 'NER', color: '#FAC775', category: 'Linguistics' },
  { id: 'Sentiment Analysis', color: '#FAC775', category: 'Linguistics' },
  { id: 'Content Moderation', color: '#FAC775', category: 'Linguistics' },
  { id: 'Text Classification', color: '#FAC775', category: 'Linguistics' },
  { id: 'Summarization', color: '#FAC775', category: 'Linguistics' },

  // ─── Data Science Sub-skills ───
  { id: 'ML Evaluation', color: '#F0997B', category: 'Data Science' },
  { id: 'Prompt Engineering', color: '#F0997B', category: 'Data Science' },
  { id: 'RLHF', color: '#F0997B', category: 'Data Science' },
  { id: 'Model Fine-Tuning', color: '#F0997B', category: 'Data Science' },
  { id: 'Data Cleaning', color: '#F0997B', category: 'Data Science' },

  // ─── Hardware Annotations Sub-skills ───
  { id: 'Sensor Data', color: '#E8A87C', category: 'Hardware Annotations' },
  { id: 'LiDAR', color: '#E8A87C', category: 'Hardware Annotations' },
  { id: 'Point Cloud', color: '#E8A87C', category: 'Hardware Annotations' },
  { id: 'Robotics', color: '#E8A87C', category: 'Hardware Annotations' },
  { id: '3D Annotation', color: '#E8A87C', category: 'Hardware Annotations' },
  { id: 'Autonomous Driving', color: '#E8A87C', category: 'Hardware Annotations' },

  // ─── AI Evaluation Sub-skills ───
  { id: 'Chatbot Evaluation', color: '#95C4B5', category: 'AI Evaluation' },
  { id: 'Safety Testing', color: '#95C4B5', category: 'AI Evaluation' },
  { id: 'Bias Detection', color: '#95C4B5', category: 'AI Evaluation' },
  { id: 'Factuality Checking', color: '#95C4B5', category: 'AI Evaluation' },

  // ─── Computer Use Sub-skills ───
  { id: 'Web Navigation', color: '#85B7EB', category: 'Computer Use' },
  { id: 'Form Filling', color: '#85B7EB', category: 'Computer Use' },
  { id: 'App Testing', color: '#85B7EB', category: 'Computer Use' },

  // ─── Tools/Platforms ───
  { id: 'Labelbox', color: '#999', category: 'Tool' },
  { id: 'CVAT', color: '#999', category: 'Tool' },
  { id: 'Label Studio', color: '#999', category: 'Tool' },
  { id: 'Scale AI', color: '#999', category: 'Tool' },
  { id: 'Encord', color: '#999', category: 'Tool' },
  { id: 'Prodigy', color: '#999', category: 'Tool' },
  { id: 'Supervisely', color: '#999', category: 'Tool' },

  // ─── Languages (subset — expand as needed) ───
  { id: 'English', color: '#999', category: 'Language' },
  { id: 'Korean', color: '#999', category: 'Language' },
  { id: 'Portuguese', color: '#999', category: 'Language' },
  { id: 'Spanish', color: '#999', category: 'Language' },
  { id: 'Japanese', color: '#999', category: 'Language' },
  { id: 'Hindi', color: '#999', category: 'Language' },
  { id: 'Mandarin', color: '#999', category: 'Language' },
  { id: 'French', color: '#999', category: 'Language' },
  { id: 'German', color: '#999', category: 'Language' },
  { id: 'Arabic', color: '#999', category: 'Language' },
];

// ─── Derived lookups ───

const _colorMap = new Map(TAG_REGISTRY.map(t => [t.id, t.color]));
const _categoryMap = new Map(TAG_REGISTRY.map(t => [t.id, t.category]));

/** Get the display color for a tag. Falls back to gray for unknown tags. */
export function getTagColor(tag) {
  return _colorMap.get(tag) || '#999';
}

/** Get the category of a tag (e.g., 'Domain', 'Audio', 'Tool'). */
export function getTagCategory(tag) {
  return _categoryMap.get(tag) || 'Other';
}

/** All tag IDs as a flat array. */
export const ALL_TAGS = TAG_REGISTRY.map(t => t.id);

/** Map of tag → color for backward compatibility with DOMAIN_COLORS pattern. */
export const TAG_COLORS = Object.fromEntries(TAG_REGISTRY.map(t => [t.id, t.color]));

/** Domain-level tags only. */
export function getDomainTags() {
  return TAG_REGISTRY.filter(t => t.category === 'Domain');
}

/** Get sub-skill tags for a given domain. */
export function getSubSkills(domain) {
  return TAG_REGISTRY.filter(t => t.category === domain);
}

/** Get tags grouped by category. */
export function getTagsByCategory() {
  const groups = {};
  for (const t of TAG_REGISTRY) {
    if (!groups[t.category]) groups[t.category] = [];
    groups[t.category].push(t);
  }
  return groups;
}

/** Check if a tag is a known registered tag. */
export function isValidTag(tag) {
  return _colorMap.has(tag);
}

/** Get all unique categories. */
export function getCategories() {
  return [...new Set(TAG_REGISTRY.map(t => t.category))];
}
